import { Icon } from './Icon';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { GameId } from '../wasm';
import './GameCheatsheet.css';
import { OtherRulePaper } from './OtherRulePapers';
import { DraughtsRulePaper } from './DraughtsRulePaper';
import { KalahRulePaper } from './KalahRulePaper';
import { ChessRulePaper } from './ChessRulePaper';
import { ShogiRulePaper } from './ShogiRulePaper';
import { RenjuRulePaper } from './RenjuRulePaper';
import { BackgammonRulePaper } from './BackgammonRulePaper';
import { PokerRulePaper } from './PokerRulePaper';
import { XiangqiRulePaper } from './XiangqiRulePaper';
import { JanggiRulePaper } from './JanggiRulePaper';
import { TraxPlaySheet } from './TraxPlaySheet';
import { JungoRulePaper, BridgeRulePaper, ChuRulePaper } from './RulePaperSheets';

export const CHEATSHEET_GAMES = ['Jungo', 'Trax', 'BridgeNT', 'ChuShogi', 'Abalone', 'Amazons', 'Gomoku', 'Kyoen', 'AnimalShogi', 'Reversi', 'Connect6', 'TwixT', 'Draughts', 'Kalah', 'Chess', 'Backgammon', 'HeadsUpPoker', 'Xiangqi', 'Janggi', 'Shogi', 'Renju'] as const;
export type CheatsheetGame = typeof CHEATSHEET_GAMES[number];
export function isCheatsheetGame(game: GameId): game is CheatsheetGame {
  return (CHEATSHEET_GAMES as readonly GameId[]).includes(game);
}
const TITLES: Record<CheatsheetGame, [string, string]> = {
  Jungo: ['囲碁', ''],
  Draughts: ['ドラフツ', ''],
  Kalah: ['マンカラ', 'カラハ'],
  Chess: ['チェス', ''],
  Shogi: ['将棋', ''],
  Renju: ['連珠', ''],
  Backgammon: ['バックギャモン', ''],
  HeadsUpPoker: ['ポーカー', 'ヘッズアップ'],
  Xiangqi: ['シャンチー', ''],
  Janggi: ['チャンギ', ''],
  AnimalShogi: ['どうぶつしょうぎ', ''],
  Reversi: ['リバーシ', ''],
  Connect6: ['六目並べ', ''],
  TwixT: ['TwixT', ''],
  Abalone: ['アバロン', ''],
  Amazons: ['アマゾンズ', ''],
  Gomoku: ['五目並べ', ''],
  Kyoen: ['共円', ''],
  Trax: ['Trax', '勝ちになる線の形'],
  BridgeNT: ['ブリッジ', ''],
  ChuShogi: ['中将棋', ''],
};

/** A reading surface only. Never register with ui-busy or send a match action. */
export function GameCheatsheet({ game, onOpenChange, description }: { game: CheatsheetGame; description?: ReactNode; onOpenChange?: (open: boolean) => void }) {
  const scrollable = ['ChuShogi', 'Draughts', 'Kalah', 'Chess', 'Backgammon', 'HeadsUpPoker', 'Xiangqi', 'Janggi', 'Shogi', 'Renju'].includes(game);
  const id = useId();
  const token = `cheatsheet-${id}`;
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement|null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const previousState = useRef<unknown>(null);
  const closing = useRef(false);

  useEffect(() => {
    onOpenChange?.(open);
    return () => onOpenChange?.(false);
  }, [open, onOpenChange]);

  useEffect(() => {
    const onPop = () => { closing.current = false; setOpen(history.state?.mixedCheatsheet === token); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (history.state?.mixedCheatsheet === token) history.replaceState(previousState.current, '');
    };
  }, [token]);

  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    if (!element) return;
    if (history.state?.mixedCheatsheet !== token) {
      previousState.current = history.state;
      history.pushState({ ...history.state, mixedCheatsheet: token }, '');
    }
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    element.showModal();
    if (scrollable) element.querySelector<HTMLElement>('.cheat-content')?.scrollTo({ top: 0 });
    close.current?.focus({ preventScroll: true });
    return () => {
      element.close();
      document.body.style.overflow = oldOverflow;
      if (opener.current?.isConnected) opener.current.focus({ preventScroll: true });
    };
  }, [open, token, scrollable]);

  const requestClose = () => {
    if (closing.current) return;
    if (history.state?.mixedCheatsheet === token) { closing.current = true; history.back(); }
    else setOpen(false);
  };
  return <>
    {description && <button type="button" className="cheat-objective-text" data-testid="cheatsheet-description" aria-haspopup="dialog" aria-expanded={open} onClick={event=>{opener.current=event.currentTarget;setOpen(true);}}>{description}</button>}
    <button ref={trigger} type="button" className="cheat-trigger" data-testid="cheatsheet-open"
      aria-label={`${TITLES[game][0]}のチートシートを開く`} aria-haspopup="dialog"
      aria-expanded={open} onClick={event=>{opener.current=event.currentTarget;setOpen(true);}}><Icon name="help" size={18}/></button>
    {createPortal(<dialog ref={dialog} className={`cheat-dialog cheat-dialog--${game}${game !== 'Trax' ? ' cheat-dialog--rules' : ''}`}
      data-testid="cheatsheet-dialog" aria-labelledby={`${id}-title`}
      onCancel={event => { event.preventDefault(); event.stopPropagation(); requestClose(); }}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key !== 'Tab') return;
        const targets = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], [tabindex="0"]')];
        if (!targets.length) return;
        event.preventDefault();
        const current = targets.indexOf(document.activeElement as HTMLElement);
        targets[(current + (event.shiftKey ? targets.length - 1 : 1)) % targets.length]?.focus();
      }} onClick={event => event.stopPropagation()}>
      <header className="cheat-header">
        <div><h1 id={`${id}-title`}>{TITLES[game][0]}</h1>{TITLES[game][1] && <p>{TITLES[game][1]}</p>}</div>
        <button ref={close} type="button" className="cheat-close" data-testid="cheatsheet-close" onClick={requestClose} aria-label="チートシートを閉じる">×</button>
      </header>
      <div className="cheat-content" data-testid="cheatsheet-content"
        tabIndex={scrollable ? 0 : undefined}
        role={scrollable ? 'region' : undefined}
        aria-label={scrollable ? TITLES[game][0] + 'のチートシート本文' : undefined}>
        {game === 'Renju' ? <RenjuRulePaper /> : game === 'Shogi' ? <ShogiRulePaper /> : game === 'Chess' ? <ChessRulePaper /> : game === 'Backgammon' ? <BackgammonRulePaper /> : game === 'HeadsUpPoker' ? <PokerRulePaper /> : game === 'Xiangqi' ? <XiangqiRulePaper /> : game === 'Janggi' ? <JanggiRulePaper /> : game === 'Kalah' ? <KalahRulePaper /> : game === 'Draughts' ? <DraughtsRulePaper /> : game === 'Jungo' ? <JungoRulePaper /> : game === 'Trax' ? <TraxPlaySheet /> : game === 'BridgeNT' ? <BridgeRulePaper /> : game === 'ChuShogi' ? <ChuRulePaper /> : <OtherRulePaper game={game} />}
      </div>
    </dialog>, document.body)}
  </>;
}
